import { Injectable, OnDestroy } from '@angular/core';
import { GroupService, IGroupModel } from './group.service';
import { Observable, ReplaySubject, Subject, throwError, zip } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { AuthorizationService } from './authorization.service';

/** Provides access to the currently active group */
@Injectable({
  providedIn: 'root',
})
export class GroupContextService implements OnDestroy {
  private groups: IGroupModel[];
  private groupsChangedSubject: Subject<IGroupModel[]> = new Subject<IGroupModel[]>();
  private allowedGroupsSubject: ReplaySubject<IGroupModel[]> = undefined;
  public allowedGroupsObservable: Observable<IGroupModel[]>;

  private _currentGroupId?: number;
  constructor(private groupService: GroupService, private authService: AuthorizationService) {
    this.setupAllowedGroupsObservable();
  }

  ngOnDestroy(): void {
    if (this.allowedGroupsSubject !== undefined) {
      this.allowedGroupsSubject.complete();
    }
  }

  /**
   * Executes the specified code (callback function) in the context of the specified
   * group id. Any http(s) requests send within this callback will include a header
   * for the given group id.
   *
   * @param groupId The group id
   * @param fn Callback function
   * @return The result of the given callback function (allows for function chaining)
   */
  public forGroup = <T>(groupId: number, fn: (groupId: number) => T): T => {
    const previousGroupId = this.currentGroupId;

    // Set the current group id for the duration of the given callback function.
    // The group header will be set (in auth-interceptor-service) for all requests
    // made during this time.
    this._currentGroupId = groupId;

    let result: T;
    try {
      result = fn(groupId);
    } finally {
      this._currentGroupId = previousGroupId;
    }

    return result;
  };

  /** Gets the current group id */
  public get currentGroupId(): number {
    return this._currentGroupId;
  }

  public set currentGroupId(groupId: number) {
    this._currentGroupId = groupId;
  }

  /** Event that is triggered when  */
  public get groupsChanged(): Subject<IGroupModel[]> {
    return this.groupsChangedSubject;
  }

  /** Returns a cached list of enabled groups */
  public getGroups = (): Promise<IGroupModel[]> => {
    if (!this.groups) {
      return this.refreshGroups().toPromise();
    }

    return Promise.resolve(this.groups);
  };

  /** Returns a cached list of enabled groups the current user has access to */
  public getAllowedGroups = async (
    includeDisabled: boolean = false,
    groupList?: IGroupModel[]
  ): Promise<IGroupModel[]> => {
    let groups = groupList ? groupList : await this.getGroups();
    groups.sort((a, b) => a.groupName.localeCompare(b.groupName));
    if (!includeDisabled) {
      groups = groups.filter(group => !group.isDisabled);
    }

    if (!this.authService.isSystemAdmin()) {
      const groupIds = this.authService.getCreateIncidentsGroups();
      groups = groups.filter(v => groupIds.includes(v.groupId));
    }
    return groups;
  };

  private setupAllowedGroupsObservable = () => {
    this.allowedGroupsSubject = new ReplaySubject<IGroupModel[]>(1);
    this.allowedGroupsObservable = this.allowedGroupsSubject.asObservable();
    this.groupsChanged.subscribe(result => {
      const authGroups = !!this.authService ? this.authService.getCreateIncidentsGroups() : [];
      const isAdmin = !!this.authService ? this.authService.isSystemAdmin() : false;

      let groups = result.filter(group => isAdmin || authGroups.includes(group.groupId));
      groups = groups.filter(group => !group.isDisabled).filter(group => !group.isDeleted);
      groups = groups.sort((a, b) => a.groupName.localeCompare(b.groupName));
      this.allowedGroupsSubject.next(groups);
    });
  };

  /** Refreshes the group list cache */
  public refreshGroups = () => {
    return this.groupService.getGroups().pipe(
      // Filter out deleted groups
      map(groups => groups.filter(group => !group.isDeleted)),

      // Fire groups changed event
      tap(groups => this.groupsChanged.next(groups)),
      tap(groups => (this.groups = groups)),
      catchError(error => {
        console.error('Failed to fetch group list');
        console.error(error);
        return throwError(error);
      })
    );
  };

  /** Returns the group name for the provided group ID */
  public getGroupName(groupId: number) {
    let groupName: string;

    let groups = this.groups;

    if (!!groups) {
      let group = groups.find(group => group.groupId == groupId);

      if (group != null) {
        groupName = group.groupName;
      }
    }

    return groupName;
  }
}
